// SSE reader for the MIAW conversation event stream.
//
// IMPORTANT: native EventSource CANNOT send an `Authorization: Bearer` header,
// and the SCRT2 SSE endpoint requires one — so we read the stream with
// fetch + ReadableStream and parse SSE frames by hand. Do not "simplify" this to
// EventSource; it's a dead end.
//
// Reconnects with exponential backoff + jitter, re-minting the token (via the
// provided MiawClient) before each reconnect, and replays from Last-Event-Id so
// no events are lost across a gap. The caller resolves any missed history via
// MiawClient.listEntries/getTranscript.

const BASE_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30000;

export default class MiawEventStream {
	// handlers: { onMessage, onTyping, onParticipant, onRouting, onStatus, onEvent }
	constructor(client, conversationId, handlers = {}) {
		this.client = client;
		this.conversationId = conversationId;
		this.handlers = handlers;
		this.controller = null;
		this.reconnectTimer = null;
		this.closed = false;
		this.attempt = 0;
		this.lastEventId = null;
	}

	setStatus(state) {
		if (this.handlers.onStatus) {
			this.handlers.onStatus(state);
		}
	}

	async start() {
		this.closed = false;
		this.attempt = 0;
		this.setStatus('connecting');
		await this.connect();
	}

	stop() {
		this.closed = true;
		if (this.reconnectTimer) {
			clearTimeout(this.reconnectTimer);
			this.reconnectTimer = null;
		}
		if (this.controller) {
			this.controller.abort();
			this.controller = null;
		}
		this.setStatus('closed');
	}

	async connect() {
		if (this.closed) {
			return;
		}
		try {
			await this.client.ensureToken();
			this.controller = new AbortController();

			// SSE lives at the SCRT2 root (/eventrouter/v1/sse), NOT under
			// /iamessage/api/v2, and streams events for the token's session (no
			// conversationId in the URL). Requires the X-Org-Id header.
			const url = `${this.client.config.scrt2URL}/eventrouter/v1/sse`;

			const headers = {
				'Authorization': `Bearer ${this.client.getToken()}`,
				'X-Org-Id': this.client.config.orgId,
				'Accept': 'text/event-stream',
			};
			if (this.lastEventId) {
				headers['Last-Event-Id'] = this.lastEventId;
			}

			const res = await fetch(url, {
				method: 'GET',
				headers,
				signal: this.controller.signal,
			});

			if (!res.ok || !res.body) {
				throw new Error(`MIAW SSE failed: ${res.status}`);
			}

			this.attempt = 0;
			this.setStatus('open');
			await this.readStream(res.body);

			// Stream closed cleanly by the server — reconnect unless we were stopped.
			if (!this.closed) {
				this.scheduleReconnect();
			}
		} catch (err) {
			if (this.closed || (err && err.name === 'AbortError')) {
				return;
			}
			this.scheduleReconnect();
		}
	}

	async readStream(body) {
		const reader = body.getReader();
		const decoder = new TextDecoder();
		let buffer = '';

		for (;;) {
			const { done, value } = await reader.read();
			if (done) {
				break;
			}
			buffer += decoder.decode(value, { stream: true });
			// Normalize CRLF→LF so frame separation ('\n\n') and field parsing work
			// regardless of the server's line endings. Re-normalizing the (small,
			// drained) retained buffer also fixes a '\r\n' split across read chunks.
			buffer = buffer.replace(/\r\n/g, '\n');

			// SSE frames are separated by a blank line.
			let sep = buffer.indexOf('\n\n');
			while (sep !== -1) {
				const raw = buffer.slice(0, sep);
				buffer = buffer.slice(sep + 2);
				this.dispatch(this.parseFrame(raw));
				sep = buffer.indexOf('\n\n');
			}
		}
	}

	parseFrame(raw) {
		const frame = { event: 'message', data: '', id: null };
		for (const line of raw.split('\n')) {
			if (!line || line.startsWith(':')) {
				continue; // blank or comment/heartbeat
			}
			const colon = line.indexOf(':');
			const field = colon === -1 ? line : line.slice(0, colon);
			let val = colon === -1 ? '' : line.slice(colon + 1);
			if (val.startsWith(' ')) {
				val = val.slice(1);
			}
			if (field === 'event') {
				frame.event = val;
			} else if (field === 'data') {
				frame.data += (frame.data ? '\n' : '') + val;
			} else if (field === 'id') {
				frame.id = val;
			}
		}
		return frame;
	}

	dispatch(frame) {
		if (frame.id) {
			this.lastEventId = frame.id;
		}

		let payload = frame.data;
		try {
			payload = frame.data ? JSON.parse(frame.data) : {};
		} catch (err) {
			// Keep the raw string if it isn't JSON.
		}

		if (this.handlers.onEvent) {
			this.handlers.onEvent(frame.event, payload);
		}

		switch (frame.event) {
			case 'CONVERSATION_MESSAGE':
				if (this.handlers.onMessage) {
					this.handlers.onMessage(payload);
				}
				break;
			case 'CONVERSATION_TYPING_STARTED_INDICATOR':
			case 'CONVERSATION_TYPING_STOPPED_INDICATOR':
				if (this.handlers.onTyping) {
					this.handlers.onTyping(frame.event.indexOf('STARTED') !== -1, payload);
				}
				break;
			case 'CONVERSATION_PARTICIPANT_CHANGED':
				if (this.handlers.onParticipant) {
					this.handlers.onParticipant(payload);
				}
				break;
			case 'CONVERSATION_ROUTING_RESULT':
				if (this.handlers.onRouting) {
					this.handlers.onRouting(payload);
				}
				break;
			default:
				break;
		}
	}

	scheduleReconnect() {
		if (this.closed) {
			return;
		}
		this.setStatus('reconnecting');
		const backoff = Math.min(MAX_BACKOFF_MS, BASE_BACKOFF_MS * Math.pow(2, this.attempt));
		const jitter = backoff * 0.25 * Math.random();
		this.attempt += 1;
		this.reconnectTimer = setTimeout(() => {
			return this.connect();
		}, backoff + jitter);
	}
}
